// src/pages/LoginPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../assets/logo/smartbusiness-white1.png";
import { Mail, Lock, LogIn, AlertCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [rememberEmail, setRememberEmail] = useState(() => {
    return localStorage.getItem("sb_remember_email") === "1";
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // where to go after login
  const from = location.state?.from?.pathname || "/admin";

  // ✅ switch mode: allow re-login even if already authenticated
  const switchAccount = !!location.state?.switchAccount;

  const expectedRole = location.state?.expectedRole || null;

  const currentRoleLabel = useMemo(() => {
    const r = auth?.user?.role;
    if (r === "admin") return "admin";
    if (r === "manager") return "manager";
    return "";
  }, [auth?.user?.role]);

  // Prefill email if enabled
  useEffect(() => {
    if (!rememberEmail) return;
    const saved = localStorage.getItem("sb_saved_email") || "";
    if (saved) setEmail(saved);
  }, [rememberEmail]);

  // ✅ If already logged in: only auto-redirect when NOT switching
  useEffect(() => {
    if (auth.loading) return;

    if (auth.isAuthenticated && !switchAccount) {
      navigate(from, { replace: true });
    }
  }, [auth.loading, auth.isAuthenticated, switchAccount, from, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Login failed");

      // optional: enforce role when switching to /admin
      if (expectedRole && data?.user?.role !== expectedRole) {
        throw new Error(`This account is not ${expectedRole}.`);
      }

      // overwrite session with new one
      auth.login(data.token, data.user);

      if (rememberEmail) {
        localStorage.setItem("sb_remember_email", "1");
        localStorage.setItem("sb_saved_email", String(email || "").trim());
      } else {
        localStorage.removeItem("sb_remember_email");
        localStorage.removeItem("sb_saved_email");
      }

      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 via-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-3xl bg-slate-900 border border-slate-800 shadow-lg mb-4">
            <img src={logo} alt="SmartBusiness" className="h-8 w-auto" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">
            {switchAccount ? "Switch account" : "Welcome back"}
          </h1>
          <p className="text-slate-600 mt-1">
            {switchAccount
              ? `You are currently logged in as ${currentRoleLabel}. Sign in to continue.`
              : "Sign in to your account"}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {switchAccount && auth.isAuthenticated && (
            <div className="mb-5 p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900">
              Signing in will replace the current session.
              <button
                type="button"
                onClick={() => auth.logout()}
                className="ml-2 underline font-semibold"
              >
                Logout first
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                Email address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
                  placeholder="admin@example.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-600 select-none">
              <input
                type="checkbox"
                checked={rememberEmail}
                onChange={(e) => setRememberEmail(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-200"
              />
              Remember email on this device
            </label>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition focus:outline-none focus:ring-4 focus:ring-blue-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className="h-5 w-5" />
                  Sign in
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-600">
            <a href="/" className="text-blue-600 hover:text-blue-700 font-medium">
              ← Back to home
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}